import type { MapIslandDto, MissionDto, QuestDto, QuestTaskDto, RecitsResponse } from '@/api/hooks'

/** Item de `RecitsResponse.recits` — un seul alias pour éviter la dérive de type. */
export type RecitItem = RecitsResponse['recits'][number]

/**
 * Indice « Où faire ça » d'une étape de quête, dérivé côté front. C'est une AIDE
 * de navigation : le matching autoritaire reste QuestProgressService (PHP) à la
 * complétion. On ne teste ici que les qualifiers CONNUS AU LANCEMENT (verb,
 * destinationType, islandId, minDanger, minShips) → un badge « en route » n'est
 * jamais « validée ».
 */
export type QuestHint =
  | { kind: 'in-flight'; mission: MissionDto }
  | { kind: 'go'; to: string; label: string; count: number }
  | null

/** Contrat d'URL Taverne↔Carte, dans un seul fichier pour éviter la dérive. */
export const MAP_ISLAND_PARAM = 'island'
export function mapIslandHref(islandId: number): string {
  return `/map?${MAP_ISLAND_PARAM}=${islandId}`
}
export function islandIdFromParam(raw: string | null): number | null {
  if (raw == null || raw === '') return null
  const n = Number(raw)
  return Number.isInteger(n) && n > 0 ? n : null
}

export const MAP_TYPE_PARAM = 'type'
export function mapTypeHref(type: string): string {
  return `/map?${MAP_TYPE_PARAM}=${type}`
}
export function typeFromParam(raw: string | null): string | null {
  return raw != null && raw.trim() !== '' ? raw : null
}

/**
 * Étapes « courantes » d'une quête : en Sequence, seule la 1ʳᵉ non-done est
 * jouable (chaîne) ; en Set, toutes les non-done le sont.
 */
export function currentTasks(quest: QuestDto): QuestTaskDto[] {
  const open = quest.tasks.filter((t) => !t.done)
  return quest.mode === 'sequence' ? open.slice(0, 1) : open
}

/**
 * Type(s) d'île éligible d'un verbe QUAND la tâche n'a pas de qualifier
 * destinationType. Miroir des cibles réelles du gameplay (cf. TRADE/SMUGGLE
 * destination types dans Map.tsx et QuestTemplateCatalog PHP). Garder synchro.
 */
const VERB_ISLAND_TYPES: Partial<Record<QuestTaskDto['verb'], string[]>> = {
  raid: ['pirate_lair'],
  smuggle: ['pirate_lair'],
  trade_buy: ['marine_port', 'merchant_outpost', 'production'],
  trade_sell: ['marine_port', 'merchant_outpost', 'production'],
}

// Coerce une valeur de qualifier en number-or-undefined. NE parse PAS les chaînes
// numériques à dessein (les qualifiers numériques arrivent déjà typés `number`).
const qualifierNum = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined)

/**
 * Îles du worldmap qui satisfont les qualifiers CONNUS AU LANCEMENT de la tâche,
 * triées par id (déterministe). Vide si aucune (ex. il faut explorer d'abord).
 */
export function eligibleIslands(task: QuestTaskDto, islands: MapIslandDto[]): MapIslandDto[] {
  const q = task.qualifier ?? {}
  const wantedIslandId = qualifierNum(q.islandId)
  const minDanger = qualifierNum(q.minDanger) ?? 0

  const byId = (a: MapIslandDto, b: MapIslandDto) => a.id - b.id
  if (wantedIslandId != null) {
    return islands.filter((i) => i.id === wantedIslandId).sort(byId)
  }

  const destType = typeof q.destinationType === 'string' ? q.destinationType : null

  return islands
    .filter((i) => {
      // explore générique (sans destinationType) = révéler une île du brouillard.
      if (task.verb === 'explore' && destType == null) return !i.isDiscovered

      const types = destType != null ? [destType] : VERB_ISLAND_TYPES[task.verb]
      if (!types || !types.includes(i.type)) return false
      if (!i.isDiscovered) return false
      if (task.verb === 'raid' && !i.raidable) return false
      if (minDanger > 0 && (i.npcDanger ?? 0) < minDanger) return false
      return true
    })
    .sort(byId)
}

/**
 * Verbe type-spécifique d'une mission — miroir de QuestProgressService::verbForMission
 * (PHP). null = patrol (n'émet que l'event générique 'mission'). Garder synchro.
 */
export function verbForMission(m: MissionDto): QuestTaskDto['verb'] | null {
  switch (m.type) {
    case 'raid_pnj': return 'raid'
    case 'smuggling': return 'smuggle'
    case 'exploration': return 'explore'
    case 'scouting': return 'explore'
    case 'recif_salvage': return 'explore'
    case 'commerce': return m.payload?.buy != null ? 'trade_buy' : 'trade_sell'
    default: return null // patrol + inconnus
  }
}

// commerce = trade_buy OU trade_sell selon payload.buy : pour l'INDICE en vol on
// reste souple (on ne connaît pas encore l'issue), donc une mission commerce
// matche les deux verbes trade_*.
const COMMERCE_VERBS = new Set<QuestTaskDto['verb']>(['trade_buy', 'trade_sell'])

/**
 * Mission in_progress dont le LANCEMENT satisfait déjà les qualifiers connus de la
 * tâche (verbe/île/type/flotte/danger). Ne garantit PAS la validation (issue/montant
 * résolus après coup). Généralise ongoingExplorationTo (hooks.ts).
 */
export function inFlightMissionForTask(
  task: QuestTaskDto, missions: readonly MissionDto[], islands: MapIslandDto[],
): MissionDto | null {
  const q = task.qualifier ?? {}
  const wantIsland = qualifierNum(q.islandId)
  const wantDest = typeof q.destinationType === 'string' ? q.destinationType : null
  const minShips = qualifierNum(q.minShips) ?? 0
  const minDanger = qualifierNum(q.minDanger) ?? 0
  const genericMission = task.verb === 'mission'

  return (
    missions.find((m) => {
      if (m.status !== 'in_progress') return false
      if (!genericMission) {
        const mv = verbForMission(m)
        if (mv == null) return false
        const verbOk = mv === task.verb || (COMMERCE_VERBS.has(task.verb) && m.type === 'commerce')
        if (!verbOk) return false
      }
      if (wantIsland != null && m.destinationIslandId !== wantIsland) return false
      const destIsland = islands.find((i) => i.id === m.destinationIslandId) ?? null
      if (wantDest != null && destIsland?.type !== wantDest) return false
      if (minShips > 0 && m.shipCount < minShips) return false
      if (minDanger > 0 && (destIsland?.npcDanger ?? 0) < minDanger) return false
      return true
    }) ?? null
  )
}

// Verbes sans lieu d'action utile → aucun indice : générique (n'importe quelle
// mission, avec ou sans flotte) et défensifs (on subit l'attaque, rien à aller faire).
const NO_HINT_VERBS = new Set<QuestTaskDto['verb']>(['mission', 'defend', 'repel_player'])
const GO_MAP: QuestHint = { kind: 'go', to: '/map', label: 'Voir sur la carte', count: 0 }

/** L'indice à afficher pour UNE tâche courante. */
export function hintForTask(
  task: QuestTaskDto, islands: MapIslandDto[], missions: readonly MissionDto[],
  recits: readonly RecitItem[] = [],
): QuestHint {
  if (NO_HINT_VERBS.has(task.verb)) return null

  const inFlight = inFlightMissionForTask(task, missions, islands)
  if (inFlight) return { kind: 'in-flight', mission: inFlight }

  if (task.verb === 'story') {
    // Récit en cours (tout état ≠ done) → on va le continuer/lire. Sinon, on n'en a
    // aucun à poursuivre → deeplink carte filtrée îles mystérieuses (c'est là qu'on
    // démarre un récit, via une expédition).
    const hasOngoing = recits.some((r) => r.state !== 'done')
    return hasOngoing
      ? { kind: 'go', to: '/recits', label: 'Continuer ton récit · Aller aux récits', count: 0 }
      : { kind: 'go', to: mapTypeHref('mysterious'), label: 'Trouver un récit · Îles mystérieuses', count: 0 }
  }
  // player_raid : pas de calcul d'éligibilité PvP en v1 → deeplink carte filtrée
  // îles joueurs (légende 'player_starter'), pas de représentant précis.
  if (task.verb === 'player_raid') {
    return { kind: 'go', to: mapTypeHref('player_starter'), label: 'Trouver un capitaine · Îles joueurs', count: 0 }
  }
  // Récif-salvage (explore + destinationType recif) → carte filtrée sur les récifs
  // plutôt qu'un récif représentatif. N'affecte PAS un explore générique (sans
  // destinationType), qui garde le comportement représentant/îles non découvertes.
  if (task.verb === 'explore' && task.qualifier?.destinationType === 'recif') {
    return { kind: 'go', to: mapTypeHref('recif'), label: 'Voir les récifs', count: 0 }
  }

  // Labels volontairement indépendants de VERB_LABELS/DEST_LABELS (ce sont des CTA).
  const eligible = eligibleIslands(task, islands)
  if (eligible.length === 0) return GO_MAP

  const rep = eligible[0]
  const extra = eligible.length > 1 ? ` (+${eligible.length - 1})` : ''
  return { kind: 'go', to: mapIslandHref(rep.id), label: `Où faire ça ? · ${rep.name}${extra}`, count: eligible.length }
}

/** Clé de dédup d'un indice pour le rendu (une ligne par cible distincte). */
function hintKey(h: NonNullable<QuestHint>): string {
  return h.kind === 'go' ? `go:${h.to}` : `in-flight:${h.mission.id}`
}

/** Dédup en préservant l'ordre (garde la 1ʳᵉ occurrence par cible). */
export function dedupeHints(hints: QuestHint[]): QuestHint[] {
  const seen = new Set<string>()
  const out: QuestHint[] = []
  for (const h of hints) {
    if (h == null) continue
    const k = hintKey(h)
    if (seen.has(k)) continue
    seen.add(k)
    out.push(h)
  }
  return out
}

/**
 * Indices d'étape courante par quête ACTIVE (id → liste dédupliquée). Les autres
 * statuts ne produisent rien.
 */
export function buildActiveQuestHints(
  quests: QuestDto[], islands: MapIslandDto[], missions: readonly MissionDto[],
  recits: readonly RecitItem[] = [],
): Record<number, QuestHint[]> {
  const out: Record<number, QuestHint[]> = {}
  for (const q of quests) {
    if (q.status !== 'active') continue
    const hints = dedupeHints(currentTasks(q).map((t) => hintForTask(t, islands, missions, recits)))
    if (hints.length > 0) out[q.id] = hints
  }
  return out
}
